import React, {useEffect, useState} from "react";
import {withDesktop} from "../../layout/Desktop";
import Box from "../../lib/appfuse-react/component/Box";
import Paper from "../../lib/appfuse-react/component/Paper";
import Survey from "../../lib/appfuse-react/component/Survey";
import CircularProgress from "../../lib/appfuse-react/component/CircularProgress";
import satisfactionSurveyService from "../../service/satisfactionSurvey/satisfactionSurveyService";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Step,
    StepLabel,
    Stepper,
    Typography
} from "@material-ui/core";
import i18n from "../../lib/appfuse-react/core/i18n";
import Toolbar from "../../component/Toolbar";
import Button from "../../lib/appfuse-react/component/Button";
import {useDispatch, useSelector} from "../../store";
import {
    removeDraft, selectData, selectDrafts,
    setData, setDraft
} from "../../reducer/satisfactionSurvey/satisfactionSurveyReducer";
import lang from "../../lib/appfuse-react/core/lang";
import {makeStyles} from "@material-ui/core/styles";
import usePrompt from "../../lib/appfuse-react/hook/usePrompt";
import useResponsive from "../../lib/appfuse-react/hook/useResponsive";
import {setRedirect} from "../../reducer/iam/authReducer";
import {ExpandMore} from "@material-ui/icons";

const useAccordionStyles = makeStyles((theme) => ({
    accordion: {
        border: `1px solid darkgray`,
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
    },
    accordionSummary: {
        backgroundColor: 'lightgray',
        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
        },
    },
    accordionDetails: {
        padding: theme.spacing(2),
        borderTop: '1px solid rgba(0, 0, 0, .125)',
    }
}));

function SatisfactionSurveyApplet() {
    const classes = useAccordionStyles();
    const dispatch = useDispatch();
    const [ready, setReady] = useState(false);
    const data = useSelector(selectData);
    const drafts = useSelector(selectDrafts);

    useEffect(() => {
        (async () => {
            const data = await satisfactionSurveyService.find();
            dispatch(setData(data));
            data.forEach(course => {
                if(lang.isUndefined(drafts[course.uuid])) {
                    const draft = {activeStep: 0};
                    dispatch(setDraft(course.uuid, draft));
                }
            });
            dispatch(setRedirect(false));
            setReady(true);
        })();
    }, []);

    const update = async () => {
        const data = await satisfactionSurveyService.find();
        dispatch(setData(data));
    };


    const responsive = useResponsive();
    const isMobile = responsive.isMediumScreen() || responsive.isSmallScreen();


    const accordionRender = data.map((course={}) => {
        const {uuid, courseCode, courseName, satisfactionSurvey} = course;
        const draft = drafts[uuid] || {};

        const handleExpand = (event, expanded) => {
            dispatch(setDraft(uuid, {...draft, expanded}));
        };

        return (
            <Accordion key={uuid} expanded={draft.expanded} onChange={handleExpand} className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Box width='8rem'>
                        <Typography>{courseCode}</Typography>
                    </Box>
                    <Box>
                        <Typography>{courseName}</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                    <SatisfactionSurveyQuestionnaire id={uuid} questionnaire={satisfactionSurvey} draft={draft} onUpdate={update}/>
                </AccordionDetails>
            </Accordion>
        );
    });

    const noSurvey = data.length!==0? null:(
        <Box display='flex' justifyContent='center' alignItems='center' height='3rem' fullWidth
             backgroundColor='lightgray' border='1px dashed darkgray'>
            <Typography>{i18n.translate('No Satisfaction Survey')}</Typography>
        </Box>);

    return (
        <Box fullWidth fullHeight overflow={'hidden'}>
            <Paper fullWidth fullHeight square overflow='hidden' variant='outlined'>
                <CircularProgress value={ready} fullWidth fullHeight>
                    { isMobile ? <Box fullHeight fullWidth display='flex' flexDirection='column' overflow='hidden' padding={2}>
                            <Typography variant='h6' noWrap>{i18n.translate('Satisfaction Survey')}</Typography>
                            {
                                data.map((course={}) => {
                                    const {uuid, courseCode, courseName, satisfactionSurvey} = course;
                                    const draft = drafts[uuid] || {};

                                    const handleExpand = (event, expanded) => {
                                        dispatch(setDraft(uuid, {...draft, expanded}));
                                    }

                                    return (
                                        <Accordion key={uuid} expanded={draft.expanded} onChange={handleExpand}>
                                            <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                                <Typography>{courseCode + ' ' + courseName}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails className={classes.accordionDetails}>
                                                {
                                                    draft.expanded && <Box>
                                                        <SatisfactionSurveyQuestionnaireMobile id={uuid} questionnaire={satisfactionSurvey} draft={draft} onUpdate={update}/>
                                                    </Box>
                                                }
                                            </AccordionDetails>
                                        </Accordion>
                                    )

                                })
                            }
                    </Box> :
                    <Box fullHeight fullWidth display='flex' flexDirection='column' overflow='hidden' padding={4}>
                        <Toolbar>
                            <Typography variant='h6' noWrap>{i18n.translate('Satisfaction Survey')}</Typography>
                        </Toolbar>
                        <Box padding={1} fullWidth fullHeight overflow='auto'>
                            {accordionRender}
                            {noSurvey}
                        </Box>
                    </Box>}
                </CircularProgress>
            </Paper>
        </Box>
    );
}

function SatisfactionSurveyQuestionnaireMobile(props) {
    const dispatch = useDispatch();
    const prompt = usePrompt();
    const {id, questionnaire, draft} = props;
    const {activeStep, expanded, change} = draft;

    const value = change || questionnaire;

    const handleChange = ({value}) => {
        dispatch(setDraft(id, { change: value, activeStep: activeStep, expanded }));
    };

    const handleComplete = async () => {
        try {
            await satisfactionSurveyService.complete(id, {satisfactionSurvey:change});
            dispatch(removeDraft(id));
            props.onUpdate();
            prompt.success('Send the data successfully.');
        } catch (e) {
            prompt.alert(e, {title:'Failed to send.'});
        }
    };

    const handleNext = () => {
        dispatch(setDraft(id, { change, activeStep: activeStep+1, expanded }));
    };
    const handleBack = () => {
        dispatch(setDraft(id, { change, activeStep: activeStep-1, expanded }));
    };

    return (
        <Box height={450} overflow={'auto'}>
            <Questionnaire questionnaire={value} activeSection={activeStep} onChange={handleChange}
                           onNext={handleNext} onBack={handleBack} onComplete={handleComplete}/>
        </Box>
    );
}

function SatisfactionSurveyQuestionnaire(props) {
    const dispatch = useDispatch();
    const prompt = usePrompt();
    const {id, questionnaire, draft} = props;
    const {activeStep, expanded, change} = draft;

    const value = change || questionnaire;

    const handleChange = ({value}) => {
        dispatch(setDraft(id, { change: value, activeStep: activeStep, expanded }));
    };

    const handleComplete = async () => {
        try {
            await satisfactionSurveyService.complete(id, {satisfactionSurvey:change});
            dispatch(removeDraft(id));
            props.onUpdate();
            prompt.success('Send the data successfully.');
        } catch (e) {
            prompt.alert(e, {title:'Failed to send.'});
        }
    };

    const handleNext = () => {
        dispatch(setDraft(id, { change, activeStep: activeStep+1, expanded }));
    };
    const handleBack = () => {
        dispatch(setDraft(id, { change, activeStep: activeStep-1, expanded }));
    };

    return (
        <Box fullWidth fullHeight display='flex' gap={4} overflow='auto'>
            <Box fullWidth width='80%'>
                <Questionnaire questionnaire={value} activeSection={activeStep} onChange={handleChange}
                               onNext={handleNext} onBack={handleBack} onComplete={handleComplete}/>
            </Box>
            <Box fullHeight width='20%'>
                <QuestionnaireStepper activeStep={activeStep}/>
            </Box>
        </Box>
    );
}

function Questionnaire(props) {
    const {questionnaire, activeSection, closeButton} = props;
    const section = questionnaire?.sections[activeSection];

    return (
        <CircularProgress value={questionnaire} fullWidth fullHeight >
            <Box paddingY={1}>
                <Typography variant='h6'>{section?.subject}</Typography>
            </Box>
            {section?.description && <Typography>{section?.description}</Typography>}
            <Survey value={questionnaire} activeSection={activeSection} onChange={props.onChange} compact/>

            <Box display='flex' justifyContent='space-between' marginTop={2}>
                <Box display='flex' gap={1}>
                    {closeButton}
                    { activeSection!==0 && <Button text='Back' color='secondary' onClick={props.onBack} /> }
                </Box>
                <Box display='flex' gap={1}>
                    { activeSection!==3 && <Button text='Next' variant='contained' color='primary' onClick={props.onNext}/> }
                    { activeSection===3 && <Button text='Complete' variant='contained' color='primary' onClick={props.onComplete}/> }
                </Box>
            </Box>
        </CircularProgress>
    );
}

const STEPS = ['Overall Evaluation', 'Course Instructor', 'Administrative Service', 'Others'];

function QuestionnaireStepper(props) {
    const {activeStep} = props;
    const steps = STEPS;

    return (
        <Stepper activeStep={activeStep} orientation="vertical" style={{backgroundColor: 'inherit'}}>
            {steps.map((label) => (
                <Step key={label}>
                    <StepLabel><Typography>{i18n.translate(label)}</Typography></StepLabel>
                </Step>
            ))}
        </Stepper>
    );
}

export default withDesktop(SatisfactionSurveyApplet);